import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Text, Flex, useToast, Skeleton, Switch, Button } from '@chakra-ui/react';
import { PageContent } from 'src/components/PageContent';
import { SectionBox } from 'src/components/SectionBox';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { breadcrumb } from './breadcrumb';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { useFiltersForm } from '@/utils/useFiltersForm';
import { GetParamsObject } from '@/utils';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { ApplicationComponentId } from '@/model/application-component';
import { useAppApplicationComponentArea, useEnableApplicationComponentMutation } from '@/services/queries/admin-config';
import { useTranslation } from 'react-i18next';
import { useAllClub } from '@/services/queries/club';
import { StoreMainBanner } from '@/model';
import { StoreBannersCreateModal } from './components/StoreBannersCreateModal';
import { StoreMainBannersTable } from './components/StoreBannersTable/StoreMainBannersTable';
import { StoreMainBannerFilters } from './types';
import { StoreMainBannersFilterBox } from './components/StoreBannersFilterBox/StoreMainBannersFilterBox';
import {
  useDeleteStoreMainBannerMutation,
  useStoreMainBannerById,
  useStoreMainBanners,
} from '@/services/queries/store-main-banner';
import { StoreBannersEditModal } from './components/StoreBannersEditModal';

export const ApplicationStoreBanners = () => {
  const { t } = useTranslation();
  const toast = useToast();

  const [isAppAreaActive, setIsAppAreaActive] = useState(false);

  const { data: appAreaData, isLoading: isLoadingAppArea } = useAppApplicationComponentArea(ApplicationComponentId.STORE_BANNERS);
  const { mutate: enableAppAreaMutation, isLoading: isLoadingEnableOrDisableAppArea } = useEnableApplicationComponentMutation();
  const [pendingAppAreaState, setPendingAppAreaState] = useState<boolean | null>(null);
  const [isDialogAppAreaState, setIsDialogAppAreaState] = useState(false);

  const cancelAppAreaChange = () => {
    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const handleAppAreaChange = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setPendingAppAreaState(isChecked);
    setIsDialogAppAreaState(true);
  };

  const confirmAppAreaChange = () => {
    if (pendingAppAreaState === null) return;

    const successActionString = pendingAppAreaState
      ? t('pages.application_store.components.store_main_banners_page.enable_success')
      : t('pages.application_store.components.store_main_banners_page.disable_success');
    const errorActionString = pendingAppAreaState
      ? t('pages.application_store.components.store_main_banners_page.enable_error')
      : t('pages.application_store.components.store_main_banners_page.disable_error');

    setIsAppAreaActive(pendingAppAreaState);

    enableAppAreaMutation([{ component_id: ApplicationComponentId.STORE_BANNERS, active: pendingAppAreaState }], {
      onSuccess: () => {
        toast({
          status: 'success',
          title: successActionString,
        });
      },
      onError: () => {
        toast({
          status: 'error',
          title: errorActionString,
        });

        setIsAppAreaActive(!pendingAppAreaState);
      },
    });

    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const [selectedItem, setSelectedItem] = useState<StoreMainBanner | undefined>(undefined);
  const [isShowEditModal, setIsShowEditModal] = useState<boolean>(false);
  const [isShowCreateModal, setIsShowCreateModal] = useState<boolean>(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);

  // filtro
  const filtersForm = useFiltersForm<StoreMainBannerFilters>({
    defaultValues: {
      team_uuid: '',
      title: '',
    },
  });

  const filterData = filtersForm.watch();

  const [sorting, setSorting] = useState<{ field: string; desc: boolean }>({ field: 'created_at', desc: true });
  const [pagination, setPagination] = useState<{ pageNumber: number; pageSize: number }>({
    pageNumber: 1,
    pageSize: 30,
  });

  const params = GetParamsObject(
    filterData,
    pagination.pageNumber,
    pagination.pageSize,
    sorting.desc ? 'desc' : 'asc',
    sorting.field,
  );

  const { data: clubs = [] } = useAllClub(true);
  const { data: bannerData, isLoading: isLoadingBannerData, forceRefetch } = useStoreMainBanners(params);

  const banners = bannerData?.data || [];

  const getItemByUUID = useStoreMainBannerById();
  const deleteMutation = useDeleteStoreMainBannerMutation();

  const onHandleEdit = useCallback(
    (item: StoreMainBanner) => {
      getItemByUUID.mutate(item.uuid, {
        onSuccess: res => {
          setSelectedItem(res);
          setIsShowEditModal(true);
        },
        onError: (resp: any) => {
          setSelectedItem(undefined);
          setIsShowEditModal(false);
          toast({
            status: 'error',
            title: (resp && resp.message) || t('pages.application_store.components.store_main_banners_page.banner_not_found'),
          });
        },
      });
    },
    [getItemByUUID, toast, t],
  );

  const onHandleDelete = useCallback((item: StoreMainBanner) => {
    setSelectedItem(item);
    setIsConfirmModalOpen(true);
  }, []);

  const onHandleEditDone = useCallback(() => {
    setIsShowEditModal(false);
    setSelectedItem(undefined);
    forceRefetch();
  }, [forceRefetch]);

  const onHandleDeleteConfirm = useCallback(() => {
    if (!selectedItem?.uuid) return;

    setIsConfirmModalOpen(false);

    deleteMutation.mutate(selectedItem.uuid, {
      onSuccess: () => {
        toast({
          title: t('pages.application_store.components.store_main_banners_page.delete_success'),
          status: 'success',
        });

        forceRefetch();
        setSelectedItem(undefined);
      },
      onError: (resp: any) => {
        toast({
          status: 'error',
          title: (resp && resp.message) || t('pages.application_store.components.store_main_banners_page.delete_error'),
        });
      },
    });
  }, [deleteMutation, forceRefetch, selectedItem, toast, t]);

  const onHandleCreateDone = useCallback(() => {
    setIsShowCreateModal(false);
    forceRefetch();
  }, [forceRefetch]);

  useEffect(() => {
    if (!appAreaData) return;

    setIsAppAreaActive(appAreaData.is_active);
  }, [appAreaData]);

  return (
    <PageContent title={t('pages.application_store.components.store_main_banners_page.page_title')} breadcrumbItems={breadcrumb}>
      <SectionBox bodyProps={{ gap: 'small' }}>
        <Flex direction={{ base: 'column', md: 'row' }} justifyContent={{ md: 'space-between' }} gap={4}>
          <Flex alignItems='center' gap={4}>
            {isLoadingAppArea ? (
              <>
                <Skeleton w='130px' h='21px' />
                <Skeleton w='102px' h='21px' />
              </>
            ) : (
              <>
                <Text fontWeight='bold' color='brand.500'>
                  {t('pages.application_store.components.store_main_banners_page.store_label')}
                </Text>

                <Switch size='sm' isChecked={isAppAreaActive} onChange={handleAppAreaChange}>
                  {isAppAreaActive
                    ? t('pages.application_store.components.store_main_banners_page.enabled')
                    : t('pages.application_store.components.store_main_banners_page.disabled')}
                </Switch>
              </>
            )}
          </Flex>

          <Flex gap={2} direction={{ base: 'column', md: 'row' }}>
            <Button
              display='flex'
              alignItems='center'
              gap={2}
              onClick={() => setIsShowCreateModal(true)}
              w={{ base: '100%', md: 'initial' }}
            >
              <ChakraFeatherIcon name='plus-circle' />
              {t('pages.application_store.components.store_main_banners_page.add_banner')}
            </Button>
          </Flex>
        </Flex>

        <SectionBox bodyProps={{ gap: 'small' }}>
          <StoreMainBannersFilterBox clubs={clubs} forceRefetch={forceRefetch} filtersForm={filtersForm} />
        </SectionBox>

        <Flex direction='column' gap='deka' mt={8}>
          <Flex direction='column' gap='micro'>
            <StoreMainBannersTable
              data={banners}
              isLoading={isLoadingBannerData}
              sorting={sorting}
              pageNumber={pagination.pageNumber}
              pageSize={pagination.pageSize}
              totalPages={bannerData?.meta.pages || 0}
              totalItems={bannerData?.meta.total || 0}
              onSortingChange={(field, desc) => setSorting({ field, desc })}
              onPaginationChange={(pageNumber, pageSize) => setPagination({ pageNumber, pageSize })}
              onDeleteClick={item => onHandleDelete(item)}
              onViewClick={item => onHandleEdit(item)}
            />
          </Flex>
        </Flex>
      </SectionBox>

      <StoreBannersCreateModal
        isOpen={isShowCreateModal}
        onClose={() => setIsShowCreateModal(false)}
        onDone={onHandleCreateDone}
      />

      {selectedItem && (
        <StoreBannersEditModal
          isOpen={!!isShowEditModal}
          onClose={() => {
            setIsShowEditModal(false);
            setSelectedItem(undefined);
          }}
          item={selectedItem}
          onDone={onHandleEditDone}
        />
      )}

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setSelectedItem(undefined);
        }}
        onConfirm={onHandleDeleteConfirm}
        isLoading={false}
        title={t('pages.application_store.components.store_main_banners_page.delete_title')}
        description={
          <Text>
            {t('pages.application_store.components.store_main_banners_page.delete_confirm')}{' '}
            <Text as='span' fontWeight='bold'>
              "{selectedItem?.title}"
            </Text>
            ?
          </Text>
        }
      />

      <ConfirmationModal
        isOpen={isDialogAppAreaState}
        onClose={cancelAppAreaChange}
        onConfirm={confirmAppAreaChange}
        title={t('pages.application_store.components.store_main_banners_page.confirm_modal_title')}
        description={
          <Text>
            {pendingAppAreaState
              ? t('pages.application_store.components.store_main_banners_page.confirm_enable')
              : t('pages.application_store.components.store_main_banners_page.confirm_disable')}
          </Text>
        }
      />

      <CenteredSpinner isLoading={isLoadingEnableOrDisableAppArea || getItemByUUID.isLoading || deleteMutation.isLoading} />
    </PageContent>
  );
};
